import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { MAX_ENTITIES_NUMBER_PER_REQUEST } from 'common/constants/exportData';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Routes } from 'common/constants/routes';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchAllFilteredSortedTenantUserMemberships, fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { ActionDefinition, Operations } from 'common/types/TypeDefs';
import { userMembershipColumnsDefinitionWithActions } from 'common/utils/userMembershipColumnsHelper';

import { useLicenseDropDownFiltersConfiguration } from './helpers';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilteredStatus,
    selectFilters } from './licenseUsersSlice';

export function LicenseUsersTab() {
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const filteredStatus = useAppSelector(selectFilteredStatus);
    const filters = useAppSelector(selectFilters);
    const [processing, setProcessing] = useState<boolean>(false);
    const [defaultSortModel] = usePalmSortModel('principalName');
    const dispatch = useAppDispatch();

    const { tenantId = '', suiteId, licenseId = '' } = useParams();

    const fetchGridData = usePalmFetchPaginatedEntities(
        'User',
        { tenantId, licenseId, status: filteredStatus, skip: 0, take: 0 },
        fetchTenantUserPaginatedMemberships,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        useLicenseDropDownFiltersConfiguration()
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchAllFilteredSortedTenantUserMemberships({
            tenantId: tenantId ?? '',
            search: search ?? '',
            status: filteredStatus,
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            licenseId: licenseId ?? '',
            signal: signal,
            rowsCount: paginationGridData?.rowsCount,
            maxNumberOfEntitiesPerRequest: MAX_ENTITIES_NUMBER_PER_REQUEST
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        if(!paginationGridData?.sortModel?.length && !model?.length) {
            return;
        }
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}/${id}/roles`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: false, urlToOpen: onGetRowLink },
    ] as ActionDefinition[];

    const columnDefinitions = userMembershipColumnsDefinitionWithActions;

    return <PalmDataGrid
        title='User'
        shouldShowCreateButton={false}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        filter={filterProp}
        operationName={Operations.Link}
        getRowLink={onGetRowLink}
        paginationMode={paginationMode}
        searchFilterDisabled={filters.keywords.length !== 0}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT,
        }}
        refreshDataGridClick={refreshDataGrid}
        sortModel={paginationGridData?.sortModel ?? defaultSortModel}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
    />;
}
